import React, { useState } from 'react';
import { Box, Typography, List, ListItemButton, ListItemText, Divider, TextField, InputAdornment, MenuItem, FormControl, Select, InputLabel, Chip, Stack } from '@mui/material';
import TableCanvas from './TableCanvas';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';

const statusMap = {
  invited: { label: 'Davetli', color: 'default' },
  confirmed: { label: 'Katılacak', color: 'success' },
  declined: { label: 'Katılmayacak', color: 'error' }
};

const SemaTab = ({ tables, setTables, participants, onTableEdit, onChange, codeHash, onEditParticipant }) => {
  // Canvas yüksekliğini otomatik ayarla (satır sayısına göre)
  const spacingY = 180;
  const startY = 60;
  const maxCols = Math.max(1, Math.floor((1024 - 60) / 260));
  const rowCount = Math.ceil(tables.length / maxCols);
  const canvasHeight = Math.max(700, startY + rowCount * spacingY + 100);

  // Atanmamış katılımcılar
  const assignedIds = tables.flatMap(t => (t.seats || []).map(s => s.participant_id).filter(Boolean));
  const unassigned = participants.filter(p => !assignedIds.includes(p.id));

  // Filtreler
  const [search, setSearch] = useState('');
  const [sideFilter, setSideFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const allSides = Array.from(new Set(unassigned.map(p => p.side).filter(Boolean)));
  const allStatuses = Array.from(new Set(unassigned.map(p => p.status).filter(Boolean)));

  // Filtreli ve sıralı katılımcılar
  const filtered = unassigned
    .filter(p =>
      (!search || (p.name && p.name.toLocaleLowerCase('tr').includes(search.toLocaleLowerCase('tr')))) &&
      (sideFilter === 'all' || p.side === sideFilter) &&
      (statusFilter === 'all' || p.status === statusFilter)
    )
    .sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));

  return (
    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
      <Box flex={3} minWidth={0}>
        <TableCanvas
          tables={tables}
          setTables={setTables}
          participants={participants}
          onTableEdit={onTableEdit}
          onChange={onChange}
          codeHash={codeHash}
          canvasHeight={canvasHeight}
        />
      </Box>
      <Box flex={1} minWidth={280}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <TextField
            size="small"
            placeholder="Misafir ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Taraf</InputLabel>
            <Select
              value={sideFilter}
              label="Taraf"
              onChange={e => setSideFilter(e.target.value)}
            >
              <MenuItem value="all">Tümü</MenuItem>
              {allSides.map(side => (
                <MenuItem key={side} value={side}>{side}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={statusFilter}
              label="Durum"
              onChange={e => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Tümü</MenuItem>
              {allStatuses.map(status => (
                <MenuItem key={status} value={status}>{statusMap[status]?.label || status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>Atanmamış Katılımcılar</Typography>
        <List>
          {filtered.map((p, idx) => {
            const side = p.side || '';
            const status = statusMap[p.status] || statusMap['invited'];
            return (
              <React.Fragment key={p.id}>
                <ListItemButton onClick={() => onEditParticipant(p)} alignItems="flex-start" sx={{ py: 2 }}>
                  <ListItemText
                    primary={
                      <Typography fontWeight={700} fontSize={18} gutterBottom>
                        {idx + 1}. {p.name}
                      </Typography>
                    }
                    secondary={
                      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        {side && <Typography variant="body2" color="text.secondary">Taraf: <b>{side}</b></Typography>}
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          sx={{ ml: 1, fontWeight: 500 }}
                        />
                      </Stack>
                    }
                  />
                  <ChevronRightIcon sx={{ color: 'grey.400', ml: 1, alignSelf: 'center' }} />
                </ListItemButton>
                <Divider />
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default SemaTab; 